import { useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { AtSign, Check, Pencil, Phone, X } from 'lucide-react'
import { Button, Input } from '@ludus/ui'
import { normalizeEmail, type Manager } from '@ludus/core'

interface ContactEditorProps {
  manager: Manager
  disabled: boolean
  onSave: (contact: { phone: string | null; email: string | null }) => Promise<boolean>
}

export function ContactEditor({ manager, disabled, onSave }: ContactEditorProps) {
  const { t } = useTranslation()
  const [editing, setEditing] = useState(false)
  const [phone, setPhone] = useState(manager.phone ?? '')
  const [email, setEmail] = useState(manager.email ?? '')
  const invalidEmail = email.trim() !== '' && !normalizeEmail(email).ok

  const open = () => {
    setPhone(manager.phone ?? '')
    setEmail(manager.email ?? '')
    setEditing(true)
  }
  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (invalidEmail) return
    const saved = await onSave({ phone: phone.trim() || null, email: email.trim() || null })
    if (saved) setEditing(false)
  }

  if (!editing) {
    return (
      <div className="flex flex-col gap-2">
        <p className="text-xs text-muted">{t('pages.managers.contact.hint')}</p>
        <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 text-sm">
          <span className="inline-flex items-center gap-1.5 text-foreground">
            <Phone className="h-3.5 w-3.5 text-muted" aria-hidden="true" />
            {manager.phone ?? <span className="text-muted">—</span>}
          </span>
          <span className="inline-flex items-center gap-1.5 text-foreground">
            <AtSign className="h-3.5 w-3.5 text-muted" aria-hidden="true" />
            {manager.email ?? <span className="text-muted">—</span>}
          </span>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-muted"
            disabled={disabled}
            aria-label={t('pages.managers.contact.edit')}
            title={t('pages.managers.contact.edit')}
            onClick={open}
          >
            <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
          </Button>
        </div>
      </div>
    )
  }

  return (
    <form
      noValidate
      className="flex flex-wrap items-center gap-2"
      onSubmit={(event) => void submit(event)}
    >
      <Input
        autoFocus
        value={phone}
        type="tel"
        onChange={(e) => setPhone(e.target.value)}
        onKeyDown={(e) => e.key === 'Escape' && setEditing(false)}
        aria-label={t('pages.managers.contact.phone')}
        placeholder={t('pages.managers.contact.phone')}
        className="h-8 w-44"
      />
      <Input
        value={email}
        type="email"
        onChange={(e) => setEmail(e.target.value)}
        onKeyDown={(e) => e.key === 'Escape' && setEditing(false)}
        aria-label={t('pages.managers.contact.email')}
        aria-invalid={invalidEmail}
        placeholder={t('pages.managers.contact.email')}
        className="h-8 w-56"
      />
      <Button type="submit" size="sm" disabled={disabled || invalidEmail}>
        <Check className="h-3.5 w-3.5" aria-hidden="true" />
        {t('pages.managers.contact.save')}
      </Button>
      <Button type="button" variant="ghost" size="sm" onClick={() => setEditing(false)}>
        <X className="h-3.5 w-3.5" aria-hidden="true" />
        {t('pages.managers.contact.cancel')}
      </Button>
      {invalidEmail && (
        <span role="alert" className="basis-full text-[11px] font-medium text-toast-error-text">
          {t('pages.managers.contact.invalidEmail')}
        </span>
      )}
    </form>
  )
}
